import "./MetroXAccountFlyout.scss";

import * as React from "react";
import { useEffect, useRef, useState } from "react";

import * as _ from "lodash";
import CountryUtil from "../../../../../uidispatcherapi/CountryUtil";
import { LogoutManualAction, SessionStore } from "../../../../../uidispatcherapi/SessionStore";
import { GdprStorage } from "../../../../../uidispatcherapi/GdprStorage";
import { useErrorHandler } from "react-error-boundary";
import {
    trackAccountSwitch,
    trackListenerRegistrationError,
    trackListenerRemovalError,
    trackLogout,
    trackMounted,
    trackScriptLoadError
} from "../../../../../utils/AccountFlyoutTrackingUtil";
import AccountFlyoutWrapper from "../fallbackFlyout/AccountFlyoutWrapper";
import jwtDecode from "jwt-decode";
import { hashString } from "../../../../../utils/util";
import { isNewAccountFlyoutFallbackEnabled } from "../../../../../featureToggles";

declare global {
    namespace JSX {
        interface IntrinsicElements {
            "cms-user-profile": any;
        }
    }
}
type Props = {
    tierLevel: string | undefined;
    idamToken: string | undefined | null;
};
const cmsScriptId = "next-cms-flyout-script";
export const MetroXAccountFlyout = (props: Props) => {
    const handler = useErrorHandler();
    const componentRef = useRef<Element | null>(null);
    const [fallback, setFallback] = useState(false);
    const eventTypeLogout = "cms:user-profile:logout";
    const eventTypeAccountSwitch = "cms:user-profile:switch";

    const handleLogout = (event: any | null | undefined): void => {
        if (event) {
            trackLogout(props.tierLevel || "");
            event.preventDefault();
        }
        if (typeof LogoutManualAction === "function") {
            LogoutManualAction();
        }
    };

    const shouldFallback = () => {
        return fallback && isNewAccountFlyoutFallbackEnabled();
    };

    const handleAccountSwitch = (event: any | null | undefined): void => {
        if (event) {
            if (props.idamToken) {
                const decodedToken: { [x: string]: any } = jwtDecode(props.idamToken);
                const personId = decodedToken.keyMap?.cdm?.person;
                if (personId) {
                    GdprStorage.localStorage.put(
                        "customer_selected_business",
                        hashString(personId).toString(),
                        GdprStorage.NECESSARY
                    );
                }
            }

            trackAccountSwitch(props.tierLevel || "");
            SessionStore.shutdownForExternalRedirect();
            window.location.reload();
        }
    };
    const addListeners = (tierLevel: string) => {
        const ref = componentRef?.current;
        if (ref) {
            ref.addEventListener(eventTypeLogout, handleLogout);
            ref.addEventListener(eventTypeAccountSwitch, handleAccountSwitch);
        } else {
            trackListenerRegistrationError(tierLevel);
        }
    };

    const addScriptAndListeners = (tierLevel: string) => {
        if (customElements && !customElements.get("cms-user-profile")) {
            const siteCoreBaseUrl = _.get(
                CountryUtil.getOptionalConfig(),
                "siteCoreBaseUrl",
                "https://compaas-rev-sc.metro.de"
            );

            const scriptFlyout = document.createElement("script");
            scriptFlyout.src = `${siteCoreBaseUrl}/data/apps/metro?sc_device=webcomponent&components=user%20profile&sso=1`;
            scriptFlyout.id = cmsScriptId;
            scriptFlyout.onerror = () => {
                trackScriptLoadError(tierLevel);
                handler(new Error("Could not load flyout script " + scriptFlyout.src));
            };
            if (document.head) {
                document.head.appendChild(scriptFlyout);
            }
            addListeners(tierLevel);
            trackMounted(tierLevel);
        } else if (customElements && customElements.get("cms-user-profile")) {
            // the component is already loaded, but with each page change the return and
            // hence the removal of the event listeners is triggered. This might be due
            // to code splitting
            addListeners(tierLevel);
        }
    };

    useEffect(() => {
        if (!shouldFallback()) {
            const timer = setTimeout(() => {
                const accountUser = window.document
                    .querySelector("cms-user-profile")
                    ?.shadowRoot?.querySelector(".account-profile");

                const cmsScript = document.getElementById(cmsScriptId);
                if (accountUser) {
                    const display = getComputedStyle(accountUser).display;
                    if (display === "none") {
                        if (cmsScript && cmsScript.parentNode) {
                            cmsScript.parentNode.removeChild(cmsScript);
                        }
                        setFallback(true);
                    }
                } else {
                    if (cmsScript && cmsScript.parentNode) {
                        cmsScript.parentNode.removeChild(cmsScript);
                    }
                    setFallback(true);
                }
            }, 4000);

            const tierLevel = props.tierLevel || "";

            addScriptAndListeners(tierLevel);

            return () => {
                clearTimeout(timer);
                const ref = componentRef?.current;
                if (ref) {
                    ref.removeEventListener(eventTypeLogout, handleLogout);
                    ref.removeEventListener(eventTypeAccountSwitch, handleAccountSwitch);
                } else {
                    trackListenerRemovalError(tierLevel);
                }
            };
        }
        return () => {
            const cmsScript = document.getElementById(cmsScriptId);
            if (cmsScript && cmsScript.parentNode) {
                cmsScript.parentNode.removeChild(cmsScript);
            }
        };
    }, []);

    const className = shouldFallback() ? "flyout-fallback open" : "flyout-fallback";
    return (
        <React.Fragment>
            <div className={className}>{isNewAccountFlyoutFallbackEnabled && <AccountFlyoutWrapper />}</div>
            {!shouldFallback() && <cms-user-profile ref={componentRef} />}
        </React.Fragment>
    );
};
